import { z } from 'zod';

/**
 * Kategorien für Template-Variablen.
 * Gruppiert Variablen nach ihrem fachlichen Kontext.
 */
export const TemplateVariableCategorySchema = z.enum([
  'base',
  'event',
  'season',
  'ticket',
  'block',
  'feedback',
  'cancellation',
]);

/** TypeScript Type für Template-Variablen-Kategorie */
export type TemplateVariableCategory = z.infer<typeof TemplateVariableCategorySchema>;

/**
 * Zod Schema für eine einzelne Template-Variable.
 * Beschreibt Schlüssel, Kategorie, Bedeutung, Beispielwert und Pflicht-Status.
 */
export const TemplateVariableSchema = z.object({
  key: z.string(),
  category: TemplateVariableCategorySchema,
  description: z.string(),
  example: z.string(),
  required: z.boolean(),
});

/** TypeScript Type für eine Template-Variable */
export type TemplateVariable = z.infer<typeof TemplateVariableSchema>;

/** Vollstaendiger Variablen-Katalog fuer Mail-Templates */
export const TEMPLATE_VARIABLES: TemplateVariable[] = [
  // Base
  {
    key: 'userName',
    category: 'base',
    description: 'Anzeigename des Empfaengers',
    example: 'Max Mustermann',
    required: true,
  },
  {
    key: 'userFirstName',
    category: 'base',
    description: 'Vorname',
    example: 'Max',
    required: true,
  },
  {
    key: 'userLastName',
    category: 'base',
    description: 'Nachname',
    example: 'Mustermann',
    required: true,
  },
  {
    key: 'userEmail',
    category: 'base',
    description: 'E-Mail-Adresse',
    example: 'max@example.com',
    required: true,
  },
  {
    key: 'currentDate',
    category: 'base',
    description: 'Heutiges Datum (deutsch)',
    example: 'Dienstag, 18.03.2026',
    required: true,
  },
  {
    key: 'portalUrl',
    category: 'base',
    description: 'Link zum Fan-Portal',
    example: 'https://fanini.live',
    required: true,
  },
  {
    key: 'discordUrl',
    category: 'base',
    description: 'Link zum Discord-Server',
    example: 'https://discord.gg/eintrachtspandau',
    required: true,
  },
  {
    key: 'supportEmail',
    category: 'base',
    description: 'Support-Kontakt',
    example: 'glenn-odya@fanini.live',
    required: true,
  },
  // Event
  {
    key: 'eventTitle',
    category: 'event',
    description:
      'Vollständiger Anzeigetitel des Events — wird automatisch aus Kategorie, Season und Titel zusammengesetzt (z.B. "ICON League Season 4 — Spieltag 7"). Falls keine Season zugeordnet ist, entspricht dies dem kurzen Titel.',
    example: 'ICON League Season 4 — Spieltag 7',
    required: false,
  },
  {
    key: 'eventShortTitle',
    category: 'event',
    description: 'Kurztitel des Events ohne Kategorie und Season (nur der direkte Titel)',
    example: 'Spieltag 7',
    required: false,
  },
  {
    key: 'eventDate',
    category: 'event',
    description: 'Datum (deutsch formatiert)',
    example: 'Sonntag, 23.03.2026',
    required: false,
  },
  {
    key: 'eventTime',
    category: 'event',
    description: 'Startzeit',
    example: '18:00',
    required: false,
  },
  {
    key: 'eventDoorTime',
    category: 'event',
    description: 'Einlasszeit',
    example: '17:00',
    required: false,
  },
  {
    key: 'eventLocation',
    category: 'event',
    description: 'Veranstaltungsort',
    example: 'Uber Arena, Berlin',
    required: false,
  },
  {
    key: 'eventDescription',
    category: 'event',
    description: 'Beschreibungstext',
    example: 'Matchday 05 der ICON League',
    required: false,
  },
  {
    key: 'eventOpponent',
    category: 'event',
    description: 'Gegner (ICON League)',
    example: 'FOKUS CLAN',
    required: false,
  },
  {
    key: 'eventCategory',
    category: 'event',
    description: 'Kategorie-Name',
    example: 'ICON League',
    required: false,
  },
  {
    key: 'eventStatus',
    category: 'event',
    description: 'Event-Status',
    example: 'draft',
    required: false,
  },
  {
    key: 'matchdayNumber',
    category: 'event',
    description: 'Spieltag-Nummer',
    example: '05',
    required: false,
  },
  {
    key: 'eventUrl',
    category: 'event',
    description: 'Link zur Event-Detail-Seite',
    example: 'https://fanini.live/events/abc123',
    required: false,
  },
  // Season
  {
    key: 'seasonName',
    category: 'season',
    description: 'Name der Season, z.B. "Season 4"',
    example: 'Season 4',
    required: false,
  },
  {
    key: 'seasonNumber',
    category: 'season',
    description: 'Saison-Nummer (aus dem Namen extrahiert)',
    example: '4',
    required: false,
  },
  // Ticket
  {
    key: 'ticketCount',
    category: 'ticket',
    description: 'Anzahl der Tickets',
    example: '2',
    required: false,
  },
  {
    key: 'ticketWord',
    category: 'ticket',
    description: '"Ticket" oder "Tickets"',
    example: 'Tickets',
    required: false,
  },
  {
    key: 'seatInfo',
    category: 'ticket',
    description: 'Sitzplatz-Info',
    example: 'Block 113, Reihe 5, Platz 12',
    required: false,
  },
  // Block
  {
    key: 'blockReason',
    category: 'block',
    description: 'Sperr-Grund',
    example: 'Unentschuldigtes Fehlen an 3 Spieltagen',
    required: false,
  },
  {
    key: 'blockEndsAt',
    category: 'block',
    description: 'Sperr-Ende (leer = permanent)',
    example: '01.06.2026',
    required: false,
  },
  {
    key: 'discordSupportUrl',
    category: 'block',
    description: 'Discord Support-Ticket Link',
    example: 'https://discord.com/channels/...',
    required: false,
  },
  // Feedback
  {
    key: 'feedbackUrl',
    category: 'feedback',
    description: 'Link zum Feedback-Formular',
    example: 'https://forms.google.com/...',
    required: false,
  },
  // Cancellation
  {
    key: 'cancellationReason',
    category: 'cancellation',
    description: 'Grund der Absage',
    example: 'Event abgesagt wegen...',
    required: false,
  },
  {
    key: 'rejectionReason',
    category: 'cancellation',
    description: 'Grund der Ablehnung',
    example: 'Kapazitaet erreicht',
    required: false,
  },
  // Sender
  {
    key: 'signature',
    category: 'base',
    description: 'HTML-Signatur des gewaehlten Absenders (wird automatisch befuellt)',
    example: '<strong>Glenn Odya</strong><br>Vorstand',
    required: false,
  },
];

/** Type fuer die Variable-Map die an die Template-Engine uebergeben wird */
export type TemplateVariablesMap = Partial<Record<string, string>>;
